import React, { useState } from 'react';
import { Box, ButtonBase, ClickAwayListener, Divider, Paper, Popper, Stack, Typography } from '@mui/material';
import { money } from './format.js';

const MetricBlock = ({ metric, dense = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const hasDetails = Boolean(metric.details?.length);
  const value = (
    <Typography
      variant="h5"
      component="span"
      sx={{
        fontWeight: 800,
        color: metric.color || 'text.primary',
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.05,
        fontSize: metric.valueSize || (dense ? 'clamp(0.92rem, 18cqw, 1.18rem)' : '1.45rem'),
        overflowWrap: 'anywhere',
      }}
    >
      {metric.value}
    </Typography>
  );
  return (
    <Box key={metric.label} sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
          fontWeight: 800,
          letterSpacing: 0,
          fontSize: 10,
          display: 'block',
          lineHeight: 1.3,
        }}
      >
        {metric.label}
      </Typography>
      {hasDetails ? (
        <ButtonBase
          aria-label={`${metric.label} details`}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            borderRadius: 0.75,
            display: 'block',
            maxWidth: '100%',
            textAlign: 'left',
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
          }}
        >
          {value}
        </ButtonBase>
      ) : value}
      {metric.sub ? (
        <Typography
          variant="caption"
          sx={{
            color: metric.subColor || 'text.secondary',
            display: 'block',
            mt: 0.25,
            lineHeight: 1.3,
            fontSize: 11,
            fontWeight: metric.subColor ? 800 : 400,
            fontVariantNumeric: metric.subColor ? 'tabular-nums' : 'normal',
          }}
        >
          {metric.sub}
        </Typography>
      ) : null}
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Paper elevation={8}>
            <Stack spacing={0.75} sx={{ p: 1.25, minWidth: 220 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase' }}>
                Round file baseline
              </Typography>
              {metric.details?.map((detail) => (
                <Stack key={detail.label} direction="row" spacing={2} justifyContent="space-between">
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{detail.label}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                    {detail.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

const MetricCard = ({
  metrics,
  accent,
  denseValues = false,
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.25,
      pb: 1.25,
      flex: 1,
      minWidth: 0,
      borderRadius: 1.5,
      bgcolor: 'background.paper',
      borderColor: 'divider',
      position: 'relative',
      overflow: 'hidden',
      containerType: 'inline-size',
    }}
  >
    {accent ? (
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: accent }} />
    ) : null}
    <Stack
      direction="row"
      spacing={1.15}
      divider={metrics.length > 1 ? <Divider orientation="vertical" flexItem /> : null}
    >
      {metrics.map((metric) => <MetricBlock key={metric.label} metric={metric} dense={denseValues} />)}
    </Stack>
  </Paper>
);

const cpqMissingBacLabel = (count) => `${count} CPQ ${count === 1 ? 'row' : 'rows'} missing BAC`;

const cycleCloseDelta = (modifiedTotals = {}) => {
  if (!modifiedTotals.loaded || modifiedTotals.gmError) return null;
  const explicit = Number(modifiedTotals.delta);
  if (Number.isFinite(explicit)) return explicit;
  return Number(modifiedTotals.gm || 0) - Number(modifiedTotals.cpq || 0);
};

const warmProgressMetrics = (contextWarmStatus = {}) => {
  const total = Number(contextWarmStatus?.total || 0);
  const warmed = Number(contextWarmStatus?.processed ?? contextWarmStatus?.warmed ?? 0);
  const pending = Math.max(0, Number(contextWarmStatus?.pending || 0));
  const queued = Math.max(0, Number(contextWarmStatus?.queued || 0));
  const processing = Math.max(0, Number(contextWarmStatus?.processing || 0));
  const stale = Math.max(0, Number(contextWarmStatus?.stale || 0));
  const failed = Math.max(0, Number(contextWarmStatus?.failed || 0));
  const unavailable = Math.max(0, Number(contextWarmStatus?.unavailable || 0));
  if (contextWarmStatus?.statusUnavailable) {
    return {
      value: 'Unavailable',
      sub: 'Warm status check failed',
      color: 'error.main',
    };
  }
  if (!total) {
    return {
      value: '--',
      sub: 'No variances to warm',
      color: 'text.secondary',
    };
  }
  const activeFresh = Math.max(0, queued + processing - stale);
  const activeText = [
    stale ? `${stale.toLocaleString()} stale` : null,
    activeFresh ? `${activeFresh.toLocaleString()} active` : null,
    failed ? `${failed.toLocaleString()} failed` : null,
    unavailable ? `${unavailable.toLocaleString()} unavailable` : null,
    pending ? `${pending.toLocaleString()} pending` : null,
  ].filter(Boolean).join(' · ');
  return {
    value: `${warmed.toLocaleString()} / ${total.toLocaleString()}`,
    sub: activeText || 'Billing context cache ready',
    color: failed || unavailable ? 'error.main' : pending || queued || processing ? 'warning.main' : 'success.main',
  };
};

const invoiceSub = (value, fallback = '--', missingCount = 0) => {
  const parsed = Number(value);
  const missing = Number(missingCount || 0);
  if (!Number.isFinite(parsed)) return missing > 0 ? 'Invoice unavailable' : fallback;
  return `${missing > 0 ? 'Partial invoice' : 'Invoice'} ${money(parsed)}`;
};

const SummaryCardsRow = ({ summary, threshold, modifiedTotals = {}, contextWarmStatus = null }) => {
  const fileDelta = cycleCloseDelta(modifiedTotals);
  const remainingDelta = fileDelta === null ? Number(summary.remainingGmCpqAbs || 0) : Math.abs(fileDelta);
  const remainingCleared = Math.abs(remainingDelta) < 0.01;
  const warmProgress = warmProgressMetrics(contextWarmStatus);
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
    <MetricCard
      metrics={[
        {
          label: 'BACs compared',
          value: summary.totalBacs.toLocaleString(),
          sub: summary.missingCount > 0 ? `${summary.missingCount} missing in a source` : 'Across all sources',
        },
        {
          label: 'Matching',
          value: summary.matching.toLocaleString(),
          sub: summary.inheritedResolved > 0
            ? `${summary.inheritedResolved.toLocaleString()} resolved from prior rounds`
            : `Within ${money(threshold)}`,
          color: 'success.main',
        },
      ]}
    />
    <MetricCard
      metrics={[
        {
          label: 'To review',
          value: summary.toReview.toLocaleString(),
          sub: 'Unreviewed variances',
          color: summary.toReview > 0 ? 'error.main' : 'text.secondary',
        },
        {
          label: 'Reviewed',
          value: summary.reviewed.toLocaleString(),
          sub: summary.clearedGmCpqAbs > 0 ? `${money(summary.clearedGmCpqAbs)} cleared` : 'Signed off',
          color: summary.reviewed > 0 ? 'success.main' : 'text.secondary',
        },
      ]}
    />
    {summary.recurringSuggestions?.total ? (
      <MetricCard
        metrics={[{
          label: 'Suggested Evidence',
          value: summary.recurringSuggestions.total.toLocaleString(),
          sub: 'Recurring matches ready',
          color: 'info.main',
        }]}
        accent="info.main"
      />
    ) : null}
    <MetricCard
      denseValues
      metrics={[
        {
          label: 'Modified GM',
          value: modifiedTotals.gmError
            ? 'Unavailable'
            : modifiedTotals.loaded
              ? money(modifiedTotals.gm || 0)
              : '--',
          sub: modifiedTotals.loaded && !modifiedTotals.gmError
            ? invoiceSub(modifiedTotals.gmInvoice, '--', modifiedTotals.gmInvoiceMissingCount)
            : '',
          subColor: Number(modifiedTotals.gmInvoiceMissingCount || 0) > 0 ? 'warning.main' : 'success.main',
          color: modifiedTotals.gmError ? 'error.main' : 'primary.main',
          details: modifiedTotals.loaded && !modifiedTotals.gmError
            ? [{ label: 'GM round file', value: money(modifiedTotals.originalGm || 0) }]
            : [],
        },
        {
          label: 'Modified CPQ',
          value: modifiedTotals.loaded ? money(modifiedTotals.cpq || 0) : '--',
          sub: modifiedTotals.loaded ? invoiceSub(modifiedTotals.cpqInvoice, '--', modifiedTotals.cpqInvoiceMissingCount) : '',
          subColor: Number(modifiedTotals.cpqInvoiceMissingCount || 0) > 0 ? 'warning.main' : 'success.main',
          color: 'secondary.main',
          details: modifiedTotals.loaded
            ? [
              { label: 'CPQ actionable rows', value: money(modifiedTotals.originalCpq || 0) },
              ...(Number(modifiedTotals.unassignedCpqCount || 0) > 0
                ? [{
                  label: cpqMissingBacLabel(Number(modifiedTotals.unassignedCpqCount || 0)),
                  value: money(modifiedTotals.unassignedCpq || 0),
                }]
                : []),
            ]
            : [],
        },
      ]}
    />
    <MetricCard
      denseValues
      metrics={[
        {
          label: 'Remaining File Δ',
          value: money(remainingDelta),
          sub: remainingCleared
            ? 'All cleared · goal $0'
            : summary.clearedGmCpqAbs > 0
            ? `${money(summary.clearedGmCpqAbs)} cleared · goal $0`
            : 'Goal: $0',
          color: remainingCleared ? 'success.main' : 'error.main',
        },
        {
          label: 'Contexts Warmed',
          value: warmProgress.value,
          sub: warmProgress.sub,
          color: warmProgress.color,
          valueSize: 'clamp(0.9rem, 16cqw, 1.12rem)',
        },
      ]}
      accent={remainingCleared ? undefined : 'error.main'}
    />
  </Stack>
  );
};

export default SummaryCardsRow;
